import { type ReactNode, forwardRef, memo } from "react";

import { HStack, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { MarkdownText } from "@/features/form-factory/components/fields/MarkdownText";
import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";

interface BaseLayoutProps {
  id: string;
  label?: string;
  error?: string | null;
  helpText?: string;
  required?: boolean;
  fieldReadonly?: boolean;
  children: ReactNode;
  testId?: string;
}

const VerticalLayout = ({
  id,
  label,
  error,
  helpText,
  required,
  fieldReadonly,
  children,
  testId,
}: BaseLayoutProps) => {
  const { t } = useTranslation(defaultNS, { keyPrefix: "component.input" });
  return (
    <VStack className="gap-1" data-testid={testId}>
      {label && (
        <Text
          variant="labelSmall"
          className="shrink-0 text-neutral-muted-on-fill"
        >
          <label htmlFor={id}>
            {label}
            {!required && !fieldReadonly && (
              <span className="ml-1">{t("optional")}</span>
            )}
          </label>
        </Text>
      )}

      {/* 入力フィールドとエラー、ヘルプテキスト */}
      <div className="w-full">
        {helpText && (
          <MarkdownText
            variant="labelSmall"
            className="text-neutral-muted-on-fill"
          >
            {helpText}
          </MarkdownText>
        )}
        <div>{children}</div>
        {error && (
          <Text variant="labelSmall" className="text-destructive">
            {error}
          </Text>
        )}
      </div>
    </VStack>
  );
};

const HorizontalLayout = ({
  id,
  label,
  error,
  helpText,
  required,
  fieldReadonly,
  children,
  testId,
}: BaseLayoutProps) => {
  const { t } = useTranslation(defaultNS, { keyPrefix: "component.input" });
  return (
    <HStack className="gap-6" data-testid={testId}>
      <VStack className="shrink-0 w-2/5">
        {label && (
          <Text variant="labelSmall" className="text-neutral-muted-on-fill">
            <label htmlFor={id}>
              {label}
              {!required && !fieldReadonly && (
                <span className="ml-1">{t("optional")}</span>
              )}
            </label>
          </Text>
        )}
        {/* エラー、ヘルプテキスト */}
        <div>
          {helpText && (
            <MarkdownText
              variant="labelSmall"
              className="text-neutral-muted-on-fill"
            >
              {helpText}
            </MarkdownText>
          )}
        </div>
      </VStack>
      <VStack className="w-full gap-2 min-w-0">
        <div>{children}</div>
        {error && (
          <Text variant="labelSmall" className="text-destructive">
            {error}
          </Text>
        )}
      </VStack>
    </HStack>
  );
};

interface FieldContainerProps extends Omit<BaseLayoutProps, "testId"> {
  /** システム的に現在読み取り専用かどうか */
  readOnly?: boolean;
  /** フィールドのレイアウト (vertical または horizontal) */
  layout?: "vertical" | "horizontal";
  /** CSSクラス名 */
  className?: string;
  /** テスト用ID */
  "data-testid"?: string;
}

/**
 * フィールドコンテナコンポーネント
 * 入力フィールドの周囲のラベル、エラー、ヘルプテキストを表示
 */
export const FieldContainer = memo(
  forwardRef<HTMLDivElement, FieldContainerProps>(function FieldContainer(
    {
      id,
      label,
      error,
      helpText,
      required,
      readOnly,
      fieldReadonly,
      layout = "vertical",
      children,
      className,
      "data-testid": testId,
    },
    ref,
  ) {
    const LayoutComponent =
      layout === "horizontal" ? HorizontalLayout : VerticalLayout;

    return (
      <div
        ref={ref}
        className={cn(
          "field-container w-full",
          readOnly || fieldReadonly ? "opacity-70 pointer-events-none" : "",
          className,
        )}
        data-testid={testId ? `${testId}-container` : undefined} // Container specific testId
      >
        <LayoutComponent
          id={id}
          label={label}
          error={error}
          helpText={helpText}
          required={required}
          testId={testId} // Pass original testId to layout components
          fieldReadonly={fieldReadonly}
        >
          {children}
        </LayoutComponent>
      </div>
    );
  }),
);

FieldContainer.displayName = "FieldContainer";
